
import React from 'react';
import { Message } from "@/types/message";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, MessagesSquare, History } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import MessageCard from "../../MessageCard";

interface CollapsedThreadGroupProps {
  thread: {
    threadId: string;
    messages: Message[];
    latestMessage: Message;
    timestamp: Date;
    hasMultipleVersions: boolean;
  };
  companyName: string;
  orderNumber?: string;
  userEmail?: string;
  sellerEmail?: string;
  getTypeIndicator: (type: string) => { icon: JSX.Element, color: string };
  getMessageSummary: (message: Message) => string;
  isExpanded: boolean;
  onToggle: () => void;
}

const CollapsedThreadGroup = ({
  thread,
  companyName,
  orderNumber = "",
  userEmail = "",
  sellerEmail = "",
  getTypeIndicator,
  getMessageSummary,
  isExpanded,
  onToggle
}: CollapsedThreadGroupProps) => {
  const latestMessage = thread.messages[thread.messages.length - 1];
  const { icon, color } = getTypeIndicator(latestMessage.type);
  const hasMultipleMessages = thread.messages.length > 1;
  
  return (
    <Collapsible 
      key={thread.threadId}
      open={isExpanded}
      onOpenChange={onToggle}
      className="rounded-lg border border-muted transition-all mb-4 relative hover:border-muted-foreground/20"
    >
      {/* Timeline dot for collapsed thread */}
      <div className={`absolute left-4 top-6 w-2 h-2 rounded-full ${color} z-10 hidden md:block`} />
      
      <div className="flex items-center p-3 bg-muted/5 rounded-t-lg">
        <div className="flex items-center flex-1 min-w-0">
          <div className="flex items-center space-x-2 text-sm">
            {icon}
            <span className="font-medium">
              {latestMessage.type === 'ai' ? 'AI Response' : 
               latestMessage.type === 'seller' ? 'Seller Reply' : 'Your Message'}
            </span>
            
            {/* Add version indicator if this is a regenerated message thread */}
            {thread.hasMultipleVersions && (
              <span className="inline-flex items-center text-xs bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground">
                <History className="h-3 w-3 mr-1" />
                v{latestMessage.version || thread.messages.length}
              </span>
            )}
          </div>
          
          {/* Add preview of message content */}
          <div className="ml-2 text-xs text-muted-foreground truncate">
            {getMessageSummary(latestMessage)}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {new Date(latestMessage.timestamp).toLocaleDateString()}
          </span>
          
          {hasMultipleMessages && (
            <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
              {thread.messages.length}
            </span>
          )}
          
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              {isExpanded ? 
                <ChevronUp className="h-4 w-4" /> : 
                <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>
      
      <CollapsibleContent>
        <div className="p-3 space-y-3">
          {/* If multiple messages, use accordion to nest them */}
          {hasMultipleMessages ? (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="messages" className="border-none">
                <AccordionTrigger className="py-2 px-0 text-sm">
                  <div className="flex items-center">
                    <MessagesSquare className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                    <span>View all messages ({thread.messages.length})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-2">
                    {thread.messages.map((message, messageIndex) => {
                      const isRegenerated = Boolean(message.relatedToMessageId);
                      
                      return (
                        <div key={message.id} className="pl-2 border-l-2 border-muted">
                          <MessageCard
                            message={{
                              ...message,
                              isRegenerated
                            }}
                            companyName={companyName}
                            isLatestAIMessage={false}
                            isLatestMessage={false}
                            orderNumber={orderNumber}
                            userEmail={userEmail}
                            sellerEmail={sellerEmail}
                            id={`message-${message.id}`}
                            showVersionInfo={thread.hasMultipleVersions}
                            versionNumber={message.version || messageIndex + 1}
                            totalVersions={thread.messages.length}
                          />
                        </div>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : (
            // If only one message, just show it directly
            <div className="pl-2">
              <MessageCard
                message={{
                  ...latestMessage,
                  isRegenerated: Boolean(latestMessage.relatedToMessageId)
                }}
                companyName={companyName}
                isLatestAIMessage={false}
                isLatestMessage={false}
                orderNumber={orderNumber}
                userEmail={userEmail}
                sellerEmail={sellerEmail}
                id={`message-${latestMessage.id}`}
              />
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default CollapsedThreadGroup;
